import { useCallback } from 'react';
import client from 'client';
import { useMutation, useQueryClient } from 'react-query';
import {
    DateSpan,
    SchedulingPreferences,
    SchedulingPreferencesData,
    SchedulingHour,
} from '../../../hooks/useSchedulingPreferences/types';
import { endOfZonedDay, groupTimeIntervalsByZonedDay, isEqualWithinMinute } from '../dateUtils';
import { formatInTimeZone } from 'date-fns-tz';

const dateToLocalDateString = (date: Date) => date.toISOString().split('T')[0];

export const dateIsEndOfDayInZone = (date: Date, timeZone: string) =>
    isEqualWithinMinute(date, endOfZonedDay(date, timeZone));
const intervalToTimeString = ({ start, end }: { start: Date; end: Date }) => {
    return {
        start: formatInTimeZone(start, 'UTC', 'HH:mm'),
        end: formatInTimeZone(end, 'UTC', 'HH:mm'),
    };
};

const addMissingDays = (
    schedulingHours: SchedulingHour[],
    schedulingPreferencesData: SchedulingPreferences
) => {
    return schedulingPreferencesData.schedulingHoursBase.map(availability => {
        const item = schedulingHours.find(groupDate => groupDate.date === availability.date);

        return (
            item || {
                date: availability.date,
                intervals: [],
            }
        );
    });
};

const prepareSchedulingHours = (schedulingHours: DateSpan[]) => {
    // group by date for server
    const utcDateIntervals = groupTimeIntervalsByZonedDay(schedulingHours, 'UTC');
    return utcDateIntervals?.reduce<SchedulingPreferencesData['schedulingHours']>(
        (intervalSets, { start, end }) => {
            const dateStr = dateToLocalDateString(start);
            let existingSet = intervalSets.find(({ date }) => date === dateStr);
            if (!existingSet) {
                existingSet = { date: dateStr, intervals: [] };
                intervalSets.push(existingSet);
            }
            existingSet.intervals.push(intervalToTimeString({ start, end }));
            return intervalSets;
        },
        []
    );
};
export const updateSchedulingPreferences = ({
    eventId,
    payload,
}: {
    eventId: number;
    payload: SchedulingPreferencesData;
}) => {
    const endpoint = `/api/v2/meetings/events/${eventId}/meetings/schedulingPreferences`;
    return client().put<SchedulingPreferencesData>(endpoint, payload);
};

const queryKey = 'schedulingPreferences';

const useUpdateSchedulingPreferences = () => {
    const queryClient = useQueryClient();
    const { mutate } = useMutation(updateSchedulingPreferences, {
        onMutate: async (variables: {
            eventId: number;
            accountId: number;
            payload: Partial<SchedulingPreferencesData>;
        }) => {
            await queryClient.cancelQueries(queryKey);
            const previousSchedulingPreferences =
                queryClient.getQueryData<SchedulingPreferencesData>(queryKey);
            queryClient.setQueryData<SchedulingPreferencesData>(queryKey, state => ({
                ...state,
                ...variables.payload,
            }));

            return { previousSchedulingPreferences };
        },
        onError: (_error, _variables, context) => {
            queryClient.setQueryData(queryKey, context.previousSchedulingPreferences);
        },
        onSettled: () => {
            queryClient.invalidateQueries(queryKey);
        },
    });

    return useCallback(
        ({
            eventId,
            payload,
            schedulingPreferencesData,
        }: {
            eventId: number;
            payload: SchedulingPreferences;
            schedulingPreferencesData: SchedulingPreferences;
        }) => {
            const schedulingHoursGroups = prepareSchedulingHours(payload.schedulingHours);
            const schedulingHours = addMissingDays(
                schedulingHoursGroups,
                schedulingPreferencesData
            );
            return mutate({
                eventId,
                payload: {
                    ...payload,
                    schedulingHours,
                },
            });
        },
        []
    );
};

export default useUpdateSchedulingPreferences;
